// @ts-nocheck

import React, { createContext, forwardRef, useEffect, useMemo } from 'react';
import { CSSTransition } from 'react-transition-group';
import styled, { css, keyframes } from 'styled-components';

import { useViewportContext } from '../../../../contexts/ViewportContext';
import useModalContext from '../../../../hooks/useModalContext';
import { computeAppBarHeight } from '../../../../utils/layout';
import {
  KLBadgeDetailsBackground,
  KLCouncilBackground,
  KLRound1Background,
  KLRound2Background,
  KLRound3Background,
  KLRound4Background,
} from '../../assets/img/backgrounds';
import { KLLockerSvg } from '../../assets/svg';
import { type KLViewType, KLViewTypes } from '../../types/view';

const backgroundPathByType = {
  [KLViewTypes.ENROLLMENT]: KLRound1Background,
  [KLViewTypes.T_RED]: KLRound1Background,
  [KLViewTypes.T_WHITE]: KLRound2Background,
  [KLViewTypes.ORIENTATION]: KLRound3Background,
  [KLViewTypes.POLE]: KLRound4Background,
  [KLViewTypes.BADGE_DETAIL]: KLBadgeDetailsBackground,
  [KLViewTypes.COUNCIL]: KLCouncilBackground,
  [KLViewTypes.ELIMINATED]: KLRound1Background,
};

const animBrightness = keyframes`
  from {
    filter: brightness(0.3);
    pointer-events: none;
  }

  to {
    filter: brightness(1);
    pointer-events: auto;
  }
`;

const appearAnim = keyframes`
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
`;

const Locker = styled(KLLockerSvg)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 5.5em;
  height: 5.5em;

  &.locker-transition-enter {
    animation: ${appearAnim} 300ms ease-in-out forwards;
  }

  &.locker-transition-exit {
    animation: ${appearAnim} 300ms ease-in-out forwards reverse;
  }
`;

const Wrapper = styled.div`
  position: relative;
  padding: 1em;
  transform: translateZ(0);
  overflow: hidden;

  ${({ $availableHeight, $availableWidth }) => css`
    width: ${$availableWidth ? `${$availableWidth}px` : '100%'};
    height: ${$availableHeight}px;
    min-height: ${$availableHeight}px;

    --KLViewWrapper-availableHeight: ${$availableHeight}px;
    --KLViewWrapper-availableWidth: ${$availableWidth}px;
    --KLViewWrapper-availableAvgDimension: calc(
      (var(--KLViewWrapper-availableHeight) + var(--KLViewWrapper-availableWidth)) / 2
    );
  `}

  ${({ $wasPreviouslyLocked, $isLocked }) =>
    $isLocked
      ? css`
          & > *:not(${Locker}) {
            filter: brightness(0.3);
          }
          pointer-events: none;
        `
      : css`
          & > *:not(${Locker}) {
            ${$wasPreviouslyLocked
              ? css`
                  animation: ${animBrightness} 1000ms ease-in-out forwards;
                `
              : undefined}
          }
        `}
`;

const Background = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: -1;

  ${({ $applyFilter }) =>
    $applyFilter
      ? css`
          filter: brightness(0.7);
        `
      : undefined}

  ${({ $backgroundPath }) =>
    $backgroundPath
      ? css`
          background-image: url(${$backgroundPath});
          background-size: cover;
          background-repeat: no-repeat;
          background-position: center;
        `
      : undefined}
`;

type KLViewContextState = {
  height: number;
};

export const KLViewContext = createContext<KLViewContextState>({ height: 0 });

type KLViewWrapperProps = {
  className?: string;
  children: React.ReactNode;
  viewType?: KLViewType;
  onClick?: () => void;
  preloadViewType?: KLViewType;
  isLocked?: boolean;
  prevIsLocked?: boolean;
};

const KLViewWrapper = forwardRef<KLViewWrapperProps, unknown>(function KLViewWrapper(
  {
    className = '',
    children,
    viewType = null,
    onClick,
    preloadViewType,
    isLocked = false,
    prevIsLocked = false,
  }: KLViewWrapperProps,
  ref,
) {
  const { height, width, ratio, domNodeRef } = useViewportContext();
  const { height: modalHeight, width: modalWidth } = useModalContext();

  const { navbarHeight, tabbarHeight } = useMemo(
    () => (ratio && domNodeRef && domNodeRef.current ? computeAppBarHeight(ratio, domNodeRef.current) : {}),
    [ratio, domNodeRef],
  );

  const availableHeight = useMemo(
    () => (modalHeight ? modalHeight : height - navbarHeight - tabbarHeight),
    [height, modalHeight, navbarHeight, tabbarHeight],
  );

  const availableWidth = useMemo(() => (modalWidth ? modalWidth : width), [modalWidth, width]);

  const providedState = useMemo(
    () => ({ height: availableHeight, width: availableWidth }),
    [availableHeight, availableWidth],
  );

  const isEnrollmentView = useMemo(() => viewType === KLViewTypes.ENROLLMENT, [viewType]);

  useEffect(() => {
    // Preload next view image to avoid blank background between transition
    if (preloadViewType) {
      const img = new Image();
      img.src = backgroundPathByType[preloadViewType];
    }
  }, [preloadViewType]);

  return (
    <Wrapper
      className={className}
      onClick={onClick}
      $availableHeight={availableHeight}
      $availableWidth={availableWidth}
      $isLocked={isLocked}
      $wasPreviouslyLocked={prevIsLocked}
      ref={ref}
    >
      <Background $backgroundPath={backgroundPathByType[viewType]} $applyFilter={isEnrollmentView} />
      <KLViewContext.Provider value={providedState}>
        {children}
        <CSSTransition classNames={'locker-transition'} in={isLocked} timeout={1000} unmountOnExit mountOnEnter>
          <Locker />
        </CSSTransition>
      </KLViewContext.Provider>
    </Wrapper>
  );
});

export default KLViewWrapper;
